import moment from 'moment';
import {BOOKING_TYPES} from '../../common/config';
import {AxiosHelperPortal} from '../../utility/helperFunctions';
import {BASE_URL_WEB} from '../../utility/constants';
import {DateTime} from 'luxon';
import {isMembershipCreditsOnlyCamp} from '../../utility/campBooking';

const previewHasCoveredMembership = preview =>
  preview?.hasCoveredMembership === true || preview?.eligible === true;

export const hasSelectedMemberForMembershipPreview = ({
  bookingType,
  isCamp,
  haveKids,
  bookingDetails,
  primaryMemberId,
}) => {
  if (bookingType === BOOKING_TYPES.APPOINTMENT) {
    return Boolean(bookingDetails?.selectedFmUuidSession);
  }

  if (bookingType !== BOOKING_TYPES.CLASS) {
    return false;
  }

  if (isCamp || haveKids) {
    return Boolean(bookingDetails?.selectedFamilyMembers?.length);
  }

  // Adult classes implicitly book the primary member and do not render a
  // registrant selector.
  return Boolean(primaryMemberId);
};

export const shouldShowMembershipCreditPreview = ({
  bookingType,
  isCamp,
  haveKids,
  bookingDetails,
  primaryMemberId,
  membershipV2Credits,
  participantPreviews = [],
  pageLoading = false,
}) => {
  if (
    pageLoading ||
    !hasSelectedMemberForMembershipPreview({
      bookingType,
      isCamp,
      haveKids,
      bookingDetails,
      primaryMemberId,
    })
  ) {
    return false;
  }

  return (
    previewHasCoveredMembership(membershipV2Credits) ||
    participantPreviews.some(participant => previewHasCoveredMembership(participant?.preview))
  );
};

export const getSessionFm = ({selectedFmUuidSession, compFm, member}) => {
  const isParentSelected = selectedFmUuidSession === '_parent';
  if (!isParentSelected) {
    const selectedFM = compFm?.find(fm => fm?.uuid === selectedFmUuidSession);
    return {
      memberFirstName: selectedFM?.firstName,
      memberLastName: selectedFM?.lastName,
      typename: BOOKING_TYPES.APPOINTMENT,
    };
  } else {
    return {
      memberFirstName: member?.firstName,
      memberLastName: member?.lastName,
      typename: BOOKING_TYPES.APPOINTMENT,
    };
  }
};

export const getSessionPayload = ({selectedPack, bookingDetails, companyData, coupon}) => {
  const selectedDate = bookingDetails?.trainingTime.date;
  const selectedTime = bookingDetails?.trainingTime.time;
  const startMoment = moment(selectedDate + ' ' + selectedTime + ':00');
  const start = startMoment.format('YYYY-MM-DDTHH:mm');
  const end = startMoment
    .add(bookingDetails.service.duration, 'minutes')
    .format('YYYY-MM-DDTHH:mm');

  let payload = {
    start: start,
    end: end,
    quantity: selectedPack?.quantity,
    price: selectedPack?.price,
    packageLabel: selectedPack?.label,
    trainerId: bookingDetails.trainer.branchTrainersTrainerId,
    status: 'active',
    from: 'clp',
    memberId: companyData.member.id,
    companyId: bookingDetails.service.companyId,
    category: 'pt',
    branchId: bookingDetails.trainer.branchTrainersBranchId,
    branchTrainerTableId: bookingDetails.trainer.id,
    trainerName: bookingDetails.trainer.trainerName,
    memberName: companyData.member.name,
    branchName: companyData.branch.name,
    companyName: companyData.name,
    timezone: bookingDetails.trainer.timezone,
    imageUrl: null,
    serviceId: bookingDetails.service.uuid,
    memberFirstName: companyData.member.firstName,
    memberLastName: companyData.member.lastName,
    serviceName: bookingDetails.service.serviceName,
    duration: bookingDetails.service.duration,
    categoryId: bookingDetails.service.categoryId,
    extraTime: 0,
    notes: '',
  };
  const isParentSelected = bookingDetails?.selectedFmUuidSession === '_parent';
  if (!isParentSelected) {
    const selectedFM = companyData?.familyMembers?.find(
      fm => fm?.uuid === bookingDetails?.selectedFmUuidSession
    );
    payload.fmID = selectedFM?.uuid;
    payload.memberFirstName = selectedFM?.firstName;
    payload.memberLastName = selectedFM?.lastName;
  }
  if (coupon != null && selectedPack?.price) {
    payload.coupon_code = coupon.coupon;
    payload.price =
      parseFloat(selectedPack?.price) - coupon.percent * 0.01 * parseFloat(selectedPack?.price);
  }
  return payload;
};

const getFilteredClass = ({bookingType, bookingDetails, group_class}) => {
  const filteredClass = group_class
    ?.filter(
      item =>
        item.uuid ===
        (bookingType === BOOKING_TYPES.APPOINTMENT
          ? bookingDetails.service?.serviceId
          : bookingDetails?.classDetails?.serviceId)
    )
    .map(item => ({
      ...item,
      name: `${item?.serviceName} - Single class pack`,
      subDescription: `Credits from this pack can only be used on ${item?.serviceName}`,
    }));

  return filteredClass;
};

export const getClassPayload = ({
  selectedPack,
  bookingType,
  bookingDetails,
  authDetails,
  coupon,
  isCamp,
  multiClassId,
  packQuantityFamilyMember,
  group_class,
  multiDayCampSchedule,
}) => {
  console.log('selectedPack345', selectedPack);
  console.log('group_class', group_class);
  const filteredClass = getFilteredClass({
    bookingType,
    bookingDetails,
    group_class,
  });
  console.log('filteredClass', filteredClass[0]);
  let payload = {
    ...bookingDetails?.classDetails,
    payment_method: 'cash',
    quantity: selectedPack?.quantity ? selectedPack?.quantity : 1,
    price: selectedPack?.price,
    packageLabel: selectedPack?.label,
    status: 'active',
    memberId: authDetails.companyData.member.id,
    companyId: authDetails.companyData.id,
    branchId: authDetails.companyData.branch.id,
    memberName:
      authDetails.companyData.member.firstName + ' ' + authDetails.companyData.member.lastName,
    branchName: authDetails.companyData.branch.name,
    companyName: authDetails.companyData.name,
    timezone: authDetails.companyData.branch.timezone,
    serviceId: filteredClass[0]?.uuid,
    memberFirstName: authDetails.companyData.member.firstName,
    memberLastName: authDetails.companyData.member.lastName,
    serviceName: bookingDetails?.classDetails?.serviceName,
    duration: bookingDetails?.classDetails?.duration,
    categoryId: filteredClass[0]?.categoryId,
    inst_pack_id: selectedPack?.id,
    extraTime: bookingDetails?.classDetails?.extraTime ?? 0,
  };

  if (coupon != null && selectedPack?.price) {
    payload.coupon_code = coupon.coupon;
    payload.discountedPrice =
      parseFloat(selectedPack?.price) - coupon.percent * 0.01 * parseFloat(selectedPack?.price);
    payload.coupon = coupon.uuid;
  }
  if (filteredClass[0]?.haveKids) {
    // Youth/Family classes: pass selected registrants
    payload.familyMembers = bookingDetails?.selectedFamilyMembers;
  } else if (isCamp && bookingDetails?.selectedFamilyMembers?.length) {
    // Adult camps: also pass selected registrant (typically primary member)
    payload.familyMembers = bookingDetails?.selectedFamilyMembers;
  }

  if (isCamp) {
    payload.bookType = selectedPack?.campBookType;
    if (multiDayCampSchedule?.scheduleToken) {
      payload.multiDayCampScheduleToken = multiDayCampSchedule.scheduleToken;
    }
    payload.payMembershipFee = selectedPack?.isOneTimeMembershipFeeEnabled;
    if (
      bookingDetails?.selectedFamilyMembers?.length +
        bookingDetails?.classDetails?.familyMembers?.length >
        1 &&
      selectedPack?.extraPersonPrice
    ) {
      payload.price = selectedPack?.extraPersonPrice;
    }
  } else {
    if (filteredClass[0]?.haveKids) {
      if (packQuantityFamilyMember > 1) {
        payload.packQuantityFamilyMember = packQuantityFamilyMember;
      }
    }
    payload.multiClassPackId = multiClassId;
  }

  return payload;
};

export const getVerifyCoupon = async (couponCode, serviceId, bookingType) => {
  const url = `${BASE_URL_WEB}verify-coupon?coupon=${couponCode}&serviceId=${serviceId}&bookingType=${bookingType}`;
  //const url = `${BASE_URL_WEB}verify-coupon?coupon=${couponCode,serviceId}`;
  const response = await AxiosHelperPortal(url, 'get');
  if (response?.data.valid) {
    return response?.data?.coupon;
  } else {
    return null;
  }
};

export const getRemainingForSessions = async ({authDetails, bookingDetails}) => {
  const memberId = authDetails.companyData?.member?.id;
  const serviceId = bookingDetails.service?.uuid;
  const companyId = bookingDetails.service?.companyId;
  const categoryId = bookingDetails.service?.categoryId;
  if (!memberId || !serviceId || !companyId || !categoryId) {
    return null;
  }
  const params = new URLSearchParams({
    memberId,
    serviceId,
    companyId,
    categoryId,
  });
  const bookingDate = bookingDetails?.trainingTime?.date;
  const selectedFmId = bookingDetails?.selectedFmUuidSession;

  if (bookingDate) {
    params.append('bookingDate', bookingDate);
  }
  if (selectedFmId && selectedFmId !== '_parent') {
    params.append('fmID', selectedFmId);
  }

  const url = `${BASE_URL_WEB}client-confirm-remaining?${params.toString()}`;
  const response = await AxiosHelperPortal(url, 'get');
  const isUnlimited = response.data.unlimited ?? response.data.isUnlimited;

  return {
    count: response.data.ptRemaining,
    showPackage: response.data.ptRemaining < 1 && !isUnlimited, // sets show package true or false
    membership_pack: response.data.membership_pack,
    membershipV2Credits: response.data.membershipV2Credits,
    paidPackageCredits: response.data.paidPackageCredits,
    totalCreditsAvailableForBooking: response.data.totalCreditsAvailableForBooking,
    isQuantityUnlimited: isUnlimited,
  };
};

export const getRemainingForClass = async ({authDetails, bookingDetails}) => {
  const classDetails = bookingDetails?.classDetails;
  const params = new URLSearchParams({
    serviceId: classDetails?.serviceId,
    groupClassId: classDetails?.uuid,
  });
  const timezone = authDetails?.companyData?.branch?.timezone;
  const bookingDate = classDetails?.start_branch_time || classDetails?.start || classDetails?.date;
  const primaryMemberId = authDetails?.companyData?.member?.id;
  const participantMemberIds = (bookingDetails?.selectedFamilyMembers || [])
    .map(participant =>
      participant?.isParent || participant?.uuid === '_parent' ? primaryMemberId : participant?.uuid
    )
    .filter(Boolean);

  if (bookingDate) {
    params.append('bookingDate', bookingDate);
  }
  if (participantMemberIds.length > 0) {
    params.append('participantMemberIds', participantMemberIds.join(','));
  }
  if (timezone) {
    params.append('timezone', timezone);
  }

  const url = `${BASE_URL_WEB}client-class-member-service?${params.toString()}`;
  console.log('member-branch-trainer url', url);
  try {
    const response = await AxiosHelperPortal(url, 'get');
    console.log('getRemainingForClass', response);
    return {
      count: response?.data?.length === 0 ? 0 : response?.data.ptRemaining,
      showPackage: true,
      membership_pack: response?.data?.membership_pack,
      membershipV2Credits: response?.data?.membershipV2Credits,
      membershipV2ParticipantPreviews: response?.data?.membershipV2ParticipantPreviews,
      multiDayCampSchedule: response?.data?.multiDayCampSchedule,
      paidPackageCredits: response?.data?.paidPackageCredits,
      totalCreditsAvailableForBooking: response?.data?.totalCreditsAvailableForBooking,
      isQuantityUnlimited: response?.data?.unlimited,
    };
  } catch (error) {
    console.log(error, error.response);
  }
};

export const getScheduledRepeatClasses = ({multiDayCampSchedule, repeatIdClasses = []}) => {
  if (!Array.isArray(multiDayCampSchedule?.days)) {
    return [...repeatIdClasses].sort((left, right) =>
      String(left?.start_branch_time || '').localeCompare(String(right?.start_branch_time || ''))
    );
  }

  const repeatClassesById = new Map(
    repeatIdClasses.map(classItem => [String(classItem?.uuid), classItem])
  );

  return multiDayCampSchedule.days.map(day => {
    const classItem = repeatClassesById.get(String(day?.groupClassId)) || {};

    return {
      ...classItem,
      uuid: day?.groupClassId,
      start_branch_time: day?.startsAt,
      end_branch_time: day?.endsAt,
      serviceName: day?.serviceName || classItem?.serviceName,
      description: day?.description ?? classItem?.description,
      participantMemberIds: day?.participantMemberIds || [],
      alreadyRegisteredMemberIds: day?.alreadyRegisteredMemberIds || [],
    };
  });
};

export const getCampScheduleNotices = multiDayCampSchedule => {
  const adjustmentReasons = new Set(
    (multiDayCampSchedule?.participants || [])
      .map(participant => participant?.adjustmentReason)
      .filter(Boolean)
  );
  const notices = [];

  if (adjustmentReasons.has('next_available_camp_day')) {
    notices.push('The start date has been set to the next available camp day');
  }
  if (adjustmentReasons.has('membership_start_date')) {
    notices.push(
      'The start date has been set to the start date of the membership covering this service. If you would like to attend camp days prior to this date, please contact your coach.'
    );
  }

  return notices;
};

export const campPacksList = ({bookingDetails}) => {
  console.log('bookingDetails', bookingDetails);
  const list = [
    {
      label: 'Book Single Event Day',
      price: parseFloat(bookingDetails?.classDetails?.single_day_price),
      campBookType: 'single',
      isOneTimeMembershipFeeEnabled: false, // user interaction
      oneTimeMembershipFee: bookingDetails?.classDetails?.one_time_fee,
    },
    {
      label: 'Book All Event Days',
      price: parseFloat(bookingDetails?.classDetails?.all_day_price),
      campBookType: 'all',
      isOneTimeMembershipFeeEnabled: false, // user interaction
      oneTimeMembershipFee: bookingDetails?.classDetails?.one_time_fee,
    },
  ];
  return list;
};

export const campPacksInstList = ({bookingDetails}) => {
  const installments = bookingDetails?.classDetails?.inst;
  if (!installments || !Array.isArray(installments)) {
    return [];
  }
  console.log('installments', installments);
  const list = installments.map((inst, index) => {
    let price;
    if (inst.isInstallment) {
      // sum of all installments
      price = inst.installments.reduce((acc, curr) => acc + curr.price, 0);
    } else {
      // full price
      price = inst.installments[0].price;
    }
    return {
      label: inst.name,
      price: parseFloat(price),
      installmentType: inst.isInstallment ? 'installment' : 'full',
      isOneTimeMembershipFeeEnabled: false,
      inst: inst,
      id: index,
    };
  });
  console.log('list', list);
  return list;
};

export const campPackDefault = ({bookingDetails}) => {
  if (!bookingDetails?.classDetails?.repeat_id) {
    return {...campPacksList({bookingDetails})[0], isSelected: true};
  } else if (
    bookingDetails?.classDetails?.repeat_id &&
    !bookingDetails?.classDetails?.allow_book_individually
  ) {
    return {...campPacksList({bookingDetails})[1], isSelected: true};
  }
};

export const validateCampAnsweredQuestions = ({
  haveKids,
  selectedFamilyMembers,
  campQuestions,
  haveQuestions,
}) => {
  console.log('haveQuestions', haveQuestions);
  let questionErr = '';
  if (haveKids && selectedFamilyMembers?.length !== 0 && campQuestions?.length !== 0) {
    const requiredQuestionIds = campQuestions?.filter(e => e.isRequired).map(e => e.uuid);
    for (let i = 0; i < selectedFamilyMembers.length; i++) {
      const currentChild = selectedFamilyMembers[i];
      for (let r = 0; r < requiredQuestionIds.length; r++) {
        if (
          haveQuestions === 'true' &&
          (!currentChild?.questions ||
            !currentChild?.questions[requiredQuestionIds[r]]?.a ||
            String(currentChild?.questions[requiredQuestionIds[r]]?.a)?.trim() === '')
        ) {
          questionErr = `You need to answer mandatory questions for ${currentChild?.firstName} ${currentChild?.lastName}`;
          return questionErr;
        }
      }
    }
  }
};

export const calculateFMQuantity = ({
  bookingDetails,
  packageQuantity,
  remainingSession,
  group_class,
}) => {
  const filteredClass = getFilteredClass({
    bookingType: 'class',
    bookingDetails,
    group_class,
  });
  if (filteredClass[0]?.haveKids !== true) {
    return 1;
  }
  let sessionQuantity = 1,
    sessionQuantityNeeded,
    quantity = 1;
  sessionQuantityNeeded =
    bookingDetails?.selectedFamilyMembers?.length > 0
      ? bookingDetails?.selectedFamilyMembers?.length
      : 1;

  const availableCredits = remainingSession?.totalCreditsAvailableForBooking;
  if (availableCredits === 'unlimited') {
    return 1;
  }
  if (Number(availableCredits ?? remainingSession?.count ?? 0) > 0) {
    sessionQuantityNeeded =
      sessionQuantityNeeded - Number(availableCredits ?? remainingSession?.count ?? 0);
  }
  if (packageQuantity != null) {
    sessionQuantity = parseInt(packageQuantity ? packageQuantity : 1);
    while (sessionQuantityNeeded > sessionQuantity) {
      sessionQuantity = sessionQuantity + parseInt(packageQuantity ? packageQuantity : 1);
      quantity = quantity + 1;
    }
  }
  return quantity;
};

export const isCreditsAvailableSession = remainingSession => {
  console.log('remainingSession', remainingSession);
  let isAvailable = false;
  if (remainingSession?.membership_pack != null) {
    isAvailable = true;
  }
  if (remainingSession?.count > 0) {
    isAvailable = true;
  }
  return isAvailable;
};

export const shouldShowRemainingPackageCredits = ({
  isCamp,
  membershipV2Credits,
  paidPackageCredits,
}) => {
  if (
    isCamp ||
    membershipV2Credits?.eligible === true ||
    membershipV2Credits?.membershipPaymentRequired === true
  ) {
    return false;
  }

  return paidPackageCredits?.unlimited === true || Number(paidPackageCredits?.count || 0) > 0;
};

export const calculateIsPackageNeeded = ({remainingSession, bookingDetails, haveKids}) => {
  const membership_pack = remainingSession?.membership_pack;

  // The aggregate Membership V2 preview knows which selected participants can
  // actually use the available credits. A large credit balance on one
  // non-sharing plan must not hide packages needed by an uncovered member.
  if (remainingSession?.membershipV2Credits?.requiresPackageCredits === true) {
    return true;
  }

  // TODO: Get membership pack from API and check if it is unlimited
  if (haveKids !== true) {
    if (membership_pack != null) {
      if (membership_pack.isQuantityUnlimited === true) {
        return false;
      } else {
        if (membership_pack.remaining != null) {
          return membership_pack.remaining <= 0;
        }
      }
    }
    return remainingSession?.count <= 0;
  } else {
    let newRemaining = remainingSession?.totalCreditsAvailableForBooking;
    if (newRemaining === 'unlimited') {
      return false;
    }
    newRemaining = Number(newRemaining ?? remainingSession?.count ?? 0);
    if (membership_pack != null) {
      if (membership_pack.isQuantityUnlimited === true) {
        return false;
      } else {
        if (membership_pack.remaining != null) {
          //newRemaining = membership_pack.remaining;
        }
      }
    }
    let sessionQuantityNeeded =
      bookingDetails?.selectedFamilyMembers?.length > 0
        ? bookingDetails?.selectedFamilyMembers?.length
        : 1;
    return newRemaining < sessionQuantityNeeded;
  }
};

export const shouldShowPackagePurchaseOptions = ({
  hasSelectedBookingMember,
  isCamp,
  remainingSession,
  bookingDetails,
  haveKids,
  pageLoading = false,
}) => {
  if (!hasSelectedBookingMember || pageLoading) {
    return false;
  }

  if (isMembershipCreditsOnlyCamp(bookingDetails?.classDetails)) {
    return false;
  }

  if (remainingSession?.membershipV2Credits?.membershipPaymentRequired === true) {
    return false;
  }

  if (!isCamp && remainingSession?.showPackage !== true) {
    return false;
  }

  return calculateIsPackageNeeded({remainingSession, bookingDetails, haveKids});
};

export const isSubmitDisabled = ({
  remainingSession,
  bookingType,
  bookingDetails,
  selectedPack,
  loading,
  isPrivateClass,
  haveKids,
}) => {
  if (remainingSession?.membershipV2Credits?.membershipPaymentRequired === true) {
    return true;
  }

  if (
    isMembershipCreditsOnlyCamp(bookingDetails?.classDetails) &&
    remainingSession?.membershipV2Credits?.eligible !== true
  ) {
    return true;
  }

  let disabled = false;
  //! if camp and is booked, can't book again
  //   if (
  //     bookingType === BOOKING_TYPES.CLASS &&
  //     bookingDetails?.classDetails?.isCamp &&
  //     bookingDetails?.classDetails?.isBooked
  //   ) {
  //     return true;
  //   }
  if (
    bookingType === BOOKING_TYPES.CLASS &&
    calculateIsPackageNeeded({remainingSession, bookingDetails, haveKids})
  ) {
    disabled = Boolean(
      typeof selectedPack === 'undefined' || selectedPack === null || isPrivateClass
    );
  }
  if (bookingType === BOOKING_TYPES.APPOINTMENT && remainingSession?.count === 0) {
    disabled = Boolean(typeof selectedPack === 'undefined' || selectedPack === null);
  }
  disabled = bookingDetails?.classDetails?.isClassFull || disabled;
  console.log('disabled', disabled);
  return loading.isSubmitBtn || disabled;
};

export const getMultiClassPackages = ({
  bookingType,
  bookingDetails,
  multi_class_pack,
  group_class,
}) => {
  if (bookingType === BOOKING_TYPES.APPOINTMENT) {
    return [{...bookingDetails.service}];
  }
  const multiClasses = [];
  multi_class_pack?.forEach(item => {
    item.subDescription = 'You can use credits from this pack across all classes';
    if (item.isAllClassPack) {
      multiClasses.push(item);
    } else if (item.selectedClasses.includes(bookingDetails?.classDetails?.serviceId)) {
      multiClasses.push(item);
    }
  });
  const groupClass = getFilteredClass({
    bookingType,
    bookingDetails,
    group_class,
  });
  return [...multiClasses, ...groupClass];
};

export const sortedInstallments = data => {
  if (!data) {
    return [];
  }
  // Separate the item with dt: null and the rest
  const nullItem = data.find(item => item.dt === null);
  const itemsWithDate = data.filter(item => item.dt !== null);

  // Sort the items with dates
  itemsWithDate.sort((a, b) => new Date(a.dt) - new Date(b.dt));

  // Combine the sorted items with the item with dt: null at the start
  return [nullItem, ...itemsWithDate];
};

export const installmentsToDisplay = inst => {
  if (!inst?.installments || !Array.isArray(inst.installments)) {
    return [];
  }

  // Current time in Eastern Time (US/Eastern)
  const nowET = DateTime.now().setZone('America/New_York');

  return inst.installments.reduce((acc, installment) => {
    // If there's no date, always treat it as "At booking"
    if (!installment.dt) {
      if (!acc[0]) {
        acc[0] = {
          dt: 'At the time of booking',
          price: 0,
          allowDiscount: inst.allowDiscount,
        };
      }
      acc[0].price += installment.price;
      return acc;
    }

    // Parse the installment date in ET
    const installmentDateET = DateTime.fromISO(installment.dt, {
      zone: 'America/New_York',
    });

    // Each installment is charged at 6:00 am ET on that particular date
    // So, define the "threshold" for that day: 6am on installmentDateET's day
    const installment6amET = installmentDateET.startOf('day').plus({hours: 6});

    // If we're already at or past that installment’s 6am ET, lump it into "At booking"
    if (nowET >= installment6amET) {
      if (!acc[0]) {
        acc[0] = {
          dt: 'At the time of booking',
          price: 0,
          allowDiscount: inst.allowDiscount,
        };
      }
      acc[0].price += installment.price;
    } else {
      // Otherwise, it’s a future installment
      acc.push({
        dt: installment.dt,
        price: installment.price,
        allowDiscount: inst.allowDiscount,
      });
    }

    return acc;
  }, []);
};

// const onRefund = async () => {
//   const payload = {
//     cancelAllAppointments: true,
//     cancelType: "refund",
//     cancelledReason: "",
//     partialRefundAmount: null,
//     refundType: "full",
//     memberId: authDetails?.companyData?.member?.id,
//     isGroupClass: "yes",
//     clientPortal: "yes",
//     groupClassId: bookingDetails?.classDetails?.uuid,
//     orderId: bookingDetails?.classDetails?.orderId,
//     uuid: bookingDetails.classDetails?.bookingId,
//     fmId: bookingDetails?.bookingFamilyMembers?.bookedFm[0]?.uuid,
//   };
//   const url = `${BASE_URL_WEB}appointment-cancel?`;
//   const response = await AxiosHelperPortal(url, "post", payload);
//   console.log("response", response);
//   if (response.data.success) {
//     history.goBack();
//     toast.success(response.data.message);
//     return;
//   }
//   toast.error(response.data.message);
// };
